// Central theme definitions - auto-loaded from tokens/themes/
// To add a new theme: create file in tokens/themes/ + add shop_items row. No other edits needed.

import { motion } from './tokens/base';
import { SHAPES, DEFAULT_SHAPE } from './tokens/shapes';
import * as contrast from './contrast';

// Re-export contrast utilities for global use
export { contrast };

// Animation presets for super themes
export const ANIMATIONS = {
  none: null,
  glowPulse: {
    name: 'glowPulse',
    keyframes: `
      @keyframes glowPulse {
        0%, 100% { box-shadow: 0 0 20px var(--theme-glow); }
        50% { box-shadow: 0 0 40px var(--theme-glow), 0 0 60px var(--theme-glow); }
      }
    `,
    duration: '2s',
    timing: 'ease-in-out',
    iteration: 'infinite',
  },
  shimmer: {
    name: 'shimmer',
    keyframes: `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `,
    duration: '3s',
    timing: 'linear',
    iteration: 'infinite',
  },
  float: {
    name: 'float',
    keyframes: `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
    `,
    duration: '3s',
    timing: 'ease-in-out',
    iteration: 'infinite',
  },
  rainbowBorder: {
    name: 'rainbowBorder',
    keyframes: `
      @keyframes rainbowBorder {
        0% { border-color: #ff0000; }
        17% { border-color: #ff8000; }
        33% { border-color: #ffff00; }
        50% { border-color: #00ff00; }
        67% { border-color: #0080ff; }
        83% { border-color: #8000ff; }
        100% { border-color: #ff0000; }
      }
    `,
    duration: '4s',
    timing: 'linear',
    iteration: 'infinite',
  },
  sparkle: {
    name: 'sparkle',
    keyframes: `
      @keyframes sparkle {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `,
    duration: '1.5s',
    timing: 'ease-in-out',
    iteration: 'infinite',
  },
  bounce: {
    name: 'bounce',
    keyframes: `
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        25% { transform: translateY(-8px); }
        35% { transform: translateY(4px); }
      }
    `,
    duration: '0.6s',
    timing: motion.easing.bounce,
    iteration: '1',
  },
  confetti: {
    name: 'confetti',
    keyframes: `
      @keyframes confetti {
        0% { transform: translateY(0) rotate(0deg); opacity: 1; }
        100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
      }
    `,
    duration: '3s',
    timing: 'ease-out',
    iteration: '1',
  },
  profileEffect: 'profileEffect',
};

// Resolve animation references in theme
const resolveAnimations = (theme) => {
  if (!theme.isSuper || !theme.animations) return theme;

  const resolved = { ...theme, animations: {} };
  for (const [key, animName] of Object.entries(theme.animations)) {
    resolved.animations[key] = typeof animName === 'string' ? ANIMATIONS[animName] : animName;
  }
  return resolved;
};

// Resolve theme with animations and shapes
const resolveTheme = (theme) => {
  let resolved = resolveAnimations(theme);
  // Resolve shape profile
  if (resolved.shape && typeof resolved.shape === 'string') {
    resolved = { ...resolved, shapes: SHAPES[resolved.shape] || DEFAULT_SHAPE };
  } else {
    resolved = { ...resolved, shapes: DEFAULT_SHAPE, shape: 'rounded' };
  }
  return resolved;
};

// All themes registry — auto-loaded from tokens/themes/*.js
const themeContext = require.context('./tokens/themes', false, /\.js$/);
export const THEMES = {};
themeContext.keys().forEach(key => {
  const themeModule = themeContext(key);
  const theme = themeModule.default || themeModule;
  if (theme && theme.id) {
    THEMES[theme.id.toLowerCase()] = resolveTheme(theme);
  }
});

// Export shapes for direct access
export { SHAPES, DEFAULT_SHAPE };

export const DEFAULT_THEME = THEMES.pink;

// Module-level app default theme (set by AppSettingsContext)
let appDefaultThemeKey = null;

export const setAppDefaultTheme = (themeKey) => {
  appDefaultThemeKey = themeKey?.toLowerCase() || null;
};

export const getAppDefaultTheme = () => appDefaultThemeKey;

// Get all super themes
export const getSuperThemes = () =>
  Object.values(THEMES).filter(t => t.isSuper);

// Get all standard themes
export const getStandardThemes = () =>
  Object.values(THEMES).filter(t => !t.isSuper);

// Utility functions
export const getTheme = (user) => {
  const userKey = user?.theme?.toLowerCase();
  if (userKey && THEMES[userKey]) return THEMES[userKey];
  if (appDefaultThemeKey && THEMES[appDefaultThemeKey]) return THEMES[appDefaultThemeKey];
  return DEFAULT_THEME;
};

export const getAccent = (user) => getTheme(user).main;

export const getBg = (user, isDark = false) => {
  const theme = getTheme(user);
  // Premium dark themes always use darkBg
  const effectiveDark = theme.isDarkTheme || isDark;
  return effectiveDark ? theme.darkBg : theme.bg;
};

// ── Contrast-aware helpers (child theme + viewer mode) ──

/**
 * True if the child's presentation context should be treated as dark.
 * Premium dark themes (isDarkTheme: true) ALWAYS return true regardless of viewer mode.
 * This ensures galaxy/ninja/etc themes keep their dark styling even when viewer is in light mode.
 */
export const getEffectiveDark = (user, viewerIsDark = false) => {
  const theme = getTheme(user);
  // Theme's isDarkTheme takes priority - premium dark themes stay dark
  return theme.isDarkTheme || viewerIsDark;
};

/** Primary text color for content displayed on a child's theme (auto-contrast). */
export const getTextColor = (user, viewerIsDark = false) => {
  const theme = getTheme(user);
  const bg = getEffectiveDark(user, viewerIsDark) ? theme.darkBg : theme.bg;
  return contrast.getContrastText(bg);
};

/** Secondary/muted text color for child's theme context (auto-contrast). */
export const getTextSecondary = (user, viewerIsDark = false) => {
  const theme = getTheme(user);
  const bg = getEffectiveDark(user, viewerIsDark) ? theme.darkBg : theme.bg;
  return contrast.getContrastTextSecondary(bg);
};

// For MUI ThemeProvider - returns palette object
export const getThemePalette = (user) => {
  const theme = getTheme(user);
  return {
    main: theme.main,
    light: theme.light,
    dark: theme.dark,
    bg: theme.bg,
  };
};

// Get CSS variables for a theme (for animations)
/** Gradient background for dark-theme shop cards. Returns undefined for light themes. */
export const getCardGradient = (user, isDark = false) => {
  const theme = getTheme(user);
  const effectiveDark = theme.isDarkTheme || isDark;
  if (!effectiveDark) return undefined;
  return `linear-gradient(135deg, ${theme.dark}ee 0%, ${theme.darkBg} 90%)`;
};

export const getThemeCSSVars = (theme) => ({
  '--theme-main': theme.main,
  '--theme-light': theme.light,
  '--theme-dark': theme.dark,
  '--theme-bg': theme.bg,
  '--theme-glow': theme.effects?.glowColor || `${theme.main}40`,
});

// Inject animation keyframes into document
export const injectAnimations = (theme) => {
  if (!theme.isSuper || !theme.animations) return;

  const styleId = 'theme-animations';
  let styleEl = document.getElementById(styleId);

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  const keyframes = Object.values(theme.animations)
    .filter(Boolean)
    .map(anim => anim.keyframes)
    .join('\n');

  styleEl.textContent = keyframes;
};
